import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  Grid,
  IconButton,
  Typography,
	DialogActions
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import CircularProgress from '@mui/material/CircularProgress';

import CustomTextField from 'components/Inputs/CustomTextField';
import CustomAutocomplete from 'components/Inputs/CustomAutocomplete';

import { addNewPipelineParams } from 'service/service';

import '../DataPipeline/dataPipeline.scss';

import { ReactComponent as WarningImage } from '../../assets/warning.svg';

type KeyValueRow = {
  paramKey: string;
  paramValue: string;
  rowId: string;
};

type Option = { jobId: string | number; jobName: string; triggerName: string; pipeline_param: any; };

export interface ConfigurePipelineProps {
  pipelines: any[],
  open: boolean;
  onClose: () => void;
  onReopen?: () => void;
  onSave: (payload: { pipelineId: string | number; keyValues: Record<string, string> }) => void;
  defaultPipelineId?: string | number;
  defaultTriggerName?: string | number;
  defaultKeyValues?: Record<string, string>;
  projectId: string | number;
}

const createRowId = (index: number) => `kv-row-${Date.now()}-${index}`;

const ConfigurePipeline: React.FC<ConfigurePipelineProps> = ({
  open,
  onClose,
  onReopen,
  onSave,
  pipelines,
  defaultPipelineId,
  defaultTriggerName,
  defaultKeyValues,
  projectId
}) => {
  const theme = useTheme();

  const textMuted = '#9e9b9bff';
  const borderMuted = '#aea9a9ff';
  const modalBackground = '#0B1D33';

  const commonInputSx = {
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: `${borderMuted} !important`,
      border: `1px solid ${borderMuted}`
    },
    '& .MuiInputBase-input::placeholder': {
      color: `${textMuted} !important`,
      opacity: 0.85
    },
    color: textMuted
  } as const;

  const autocompleteInputSx = {
    ...commonInputSx,
    '& .MuiAutocomplete-popupIndicator, & .MuiAutocomplete-popupIndicator svg': {
      color: `${textMuted} !important`,
      fill: `${textMuted} !important`
    },
    '& .MuiAutocomplete-clearIndicator, & .MuiAutocomplete-clearIndicator svg': {
      color: `${textMuted} !important`,
      fill: `${textMuted} !important`
    }
  } as const;

  const outlinedDangerIconSx = {
    color: theme.palette.error.main,
    border: `1px solid ${theme.palette.error.main}`,
    borderRadius: '6px',
    p: '2px',
    '&:hover': { backgroundColor: 'transparent' }
  } as const;

  // ---- Pipelines dropdown state ----
  const [pipelineOptions, setPipelineOptions] = useState<Option[]>([]);
  const [isLoadingPipelines, setIsLoadingPipelines] = useState(false);
  const [selectedPipelineId, setSelectedPipelineId] = useState<string | number>(defaultPipelineId ?? '');
  const [selectedTriggerName, setSelectedTriggerName] = useState<string | number>(defaultTriggerName ?? '');
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [onSaveLoader, setOnSaveLoader] = useState(false);

  const loadInitialPipelines = useCallback(async () => {
    setIsLoadingPipelines(true);
    try {
      setPipelineOptions(pipelines || []);
      // Prefer explicit defaultPipelineId prop when modal opens, else the current selectedPipelineId
      const selId = (defaultPipelineId ?? selectedPipelineId) ? String(defaultPipelineId ?? selectedPipelineId) : '';
      const triggerName = (defaultTriggerName ?? selectedTriggerName) ? String(defaultTriggerName ?? selectedTriggerName) : '';

      if (selId) {
        // find matching pipeline by jobId + triggerName (if triggerName exists)
        const opt = (pipelines || []).find(o => {
          const isJobMatch = String(o.jobId) === selId;
          const isTriggerMatch =
            !triggerName || triggerName === '' || String(o.triggerName ?? '') === String(triggerName);
          return isJobMatch && isTriggerMatch;
        });

        if (opt?.pipeline_param && Object.keys(opt.pipeline_param).length) {
          const params = Object.entries(opt.pipeline_param).map(([k, v], i) => ({
            paramKey: k,
            paramValue: typeof v === 'string' ? v : JSON.stringify(v ?? ''),
            rowId: createRowId(i)
          }));

          setKeyValueRows(params);
          setSelectedPipelineId(String(opt.jobId));
          setSelectedTriggerName(String(opt.triggerName));
        }
      }

    } finally {
      setIsLoadingPipelines(false);
    }
  }, [pipelines, defaultPipelineId, selectedPipelineId]);


  // Current selected option object for Autocomplete binding
  const selectedPipelineOption = useMemo(
    () => pipelineOptions.find(o => String(o.jobId) === String(selectedPipelineId)) ?? null,
    [pipelineOptions, selectedPipelineId]
  );

// For delete confirmation dialog
	const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
	const [rowToDelete, setRowToDelete] = useState<string | null>(null);
	const [isKeyValueError, setIsKeyValueError] = useState(false);

  // Load when modal opens
  useEffect(() => {
    if (!open) {
      setSuccessMsg(null);
      return;
    }
    loadInitialPipelines();
  }, [open, loadInitialPipelines]);

  // Sync prop -> state whenever defaultPipelineId changes or when dialog opens
  useEffect(() => {
    // When dialog opens, respect a passed default; when it closes, clear it
    if (open) {
      if (defaultPipelineId !== undefined && defaultPipelineId !== null && defaultPipelineId !== '') {
        setSelectedPipelineId(String(defaultPipelineId));
      }
      if (defaultTriggerName !== undefined && defaultTriggerName !== null && defaultTriggerName !== '') {
        setSelectedTriggerName(String(defaultTriggerName));
      }
    } else {
      // ensure we clear selection when dialog closes
      setSelectedPipelineId('');
      setSelectedTriggerName('');
    }
  }, [defaultPipelineId, open]);

  const handleOnClose = () => {
    console.log('closing');
    // clear internal state first
    setSelectedPipelineId('');
    setSelectedTriggerName('');
    setKeyValueRows([{ paramKey: '', paramValue: '', rowId: createRowId(0) }]);
    setSuccessMsg(null);
    setOnSaveLoader(false);

    // then notify parent
    onClose();
  }

  const onChangePipeline = (sel: Option | null) => {
    if (!sel) {
      setSelectedPipelineId('');
      setSelectedTriggerName('');
      setKeyValueRows([{ paramKey: '', paramValue: '', rowId: createRowId(0) }]);
      return;
    }
    setSelectedPipelineId(String(sel.jobId));
    setSelectedTriggerName(String(sel.triggerName));
    const params = Object.entries(sel.pipeline_param || {}).map(([k, v], i) => ({
      paramKey: k,
      paramValue: typeof v === 'string' ? v : JSON.stringify(v ?? ''),
      rowId: createRowId(i)
    }));
    setKeyValueRows(params.length ? params : [{ paramKey: '', paramValue: '', rowId: createRowId(0) }]);
  };

  // ---- Key/Value rows state ----
  const [keyValueRows, setKeyValueRows] = useState<KeyValueRow[]>(
    defaultKeyValues
      ? Object.entries(defaultKeyValues).map(([k, v], i) => ({
          paramKey: k,
          paramValue: String(v ?? ''),
          rowId: createRowId(i)
        }))
      : [{ paramKey: '', paramValue: '', rowId: createRowId(0) }]
  );
  
  const handleAddRow = () =>
    setKeyValueRows(prev => [
      ...prev,
      { paramKey: '', paramValue: '', rowId: createRowId(prev.length) }
    ]);

  const handleRemoveRow = (rowId: string) =>
    setKeyValueRows(prev => prev.filter(r => r.rowId !== rowId));

  function updateRowField(prev: KeyValueRow[], rowId: string, field: 'paramKey' | 'paramValue', value: string) {
    return prev.map(r => (r.rowId === rowId ? { ...r, [field]: value } : r));
  }

  const handleChangeRowField =
    (rowId: string, field: 'paramKey' | 'paramValue') =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setKeyValueRows(prev => updateRowField(prev, rowId, field, e.target.value));
    }

  const updateRowAndCheckError = (prev: KeyValueRow[], rowId: string, field: 'paramKey' | 'paramValue', trimmed: string) => {
    const newRows = prev.map(r => (r.rowId === rowId ? { ...r, [field]: trimmed } : r));

    // Compute if any row violates limits
    const hasError = newRows.some(r => {
      const keyLen = (r.paramKey ?? '').length;
      const valueLen = (r.paramValue ?? '').length;
      return keyLen > 64 || valueLen > 500;
    });

    // Update the global error flag based on the whole array
    setIsKeyValueError(hasError);

    return newRows;
  };

  const handleTrimOnBlur =
    (rowId: string, field: 'paramKey' | 'paramValue') =>
    (e: React.FocusEvent<HTMLInputElement>) => {
      const trimmed = e.target.value.trim();
      setKeyValueRows(prev => updateRowAndCheckError(prev, rowId, field, trimmed));
    };



  const handleSave = async () => {
    if (!selectedPipelineId) return;

    setOnSaveLoader(true)
    const pipelineParams: Record<string, string> = {};
    for (const r of keyValueRows) {
      const k = r.paramKey.trim();
      const v = r.paramValue.trim();
      if (k && v) pipelineParams[k] = v;
    };

    const requestBody = {
      Data: [
        {
          JobId: selectedPipelineId,
          ProjectId: projectId,
          pipeline_param: pipelineParams,
          Trigger_Name: selectedTriggerName
        }
      ]
    };

    try {
      const resp = await addNewPipelineParams(requestBody);
      if (resp?.success) {
        setSuccessMsg(resp?.data?.message ?? 'Successfully saved pipeline parameters.');
      } else {
        setSuccessMsg(null);
      }
      onSave({ pipelineId: selectedPipelineId, keyValues: pipelineParams });
      setSelectedPipelineId('');
      setSelectedTriggerName('');
      setKeyValueRows([{ paramKey: '', paramValue: '', rowId: createRowId(0) }]);
      onClose();
      setOnSaveLoader(false);
    } catch {
      // Let parent toast/snackbar handle failure if needed
    }
  };

  return (
    <>
    <Dialog
      open={open}
      onClose={handleOnClose}
      PaperProps={{
        sx: {
          width: '650px',
          maxWidth: '650px',
        },
      }}
      sx={{
        '& .MuiPaper-root': {
          width: '650px'
        }
      }}
    >
      <DialogTitle bgcolor="primary.main" sx={{ p: '8px 16px !important' }}>
        <Box display="flex" alignItems="center">
          <Typography variant="widgetTitle" flexGrow={1}>
            Configure Pipeline
          </Typography>
          <IconButton onClick={handleOnClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{ pt: 3, backgroundColor: modalBackground, color: textMuted }}
      >
        {/* Select Pipeline */}
        <Box mb={3}>
          <Typography variant="pageTitle" sx={{ fontSize: 16, mb: 1, color: theme.palette.primary.light }} />
          <CustomAutocomplete
            fullWidth
            sx={autocompleteInputSx}
            options={pipelineOptions}
            value={selectedPipelineOption}
            getOptionLabel={(opt: { jobId: string | number; jobName: string }) => opt?.jobName || ''}
            isOptionEqualToValue={(a, b) => String(a?.jobId) === String(b?.jobId)}
            onChange={(_e, sel: Option | null) => onChangePipeline(sel)}
            loading={isLoadingPipelines}
            onOpen={loadInitialPipelines}
            noOptionsText="Select Pipeline Name"
            renderInput={(params: any) => (
              <CustomTextField
                {...params}
                placeholder="Select Pipeline*"
                sx={commonInputSx}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isLoadingPipelines ? <CircularProgress size={16} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  )
                }}
              />
            )}
          />
        </Box>

        {/* Configure Key–Value */}
        <Box mb={3} display="flex" alignItems="center">
          <Typography
            variant="subHeader"
            sx={{ color: theme.palette.primary.light, mr: 1.5, whiteSpace: 'nowrap' }}
          >
            Configure Key-Value
          </Typography>
          <Box
            sx={{
              flexGrow: 1,
              height: '1px',
              backgroundColor: theme.palette.secondary.light,
              opacity: 0.6,
              transform: 'translateY(-1px)'
            }}
          />
        </Box>

        {/* Key–Value Rows */}
        <Grid container spacing={2}>
          {keyValueRows.map(row => (
            <React.Fragment key={row.rowId}>
              <Grid item xs={5}>
                <CustomTextField
                  fullWidth
                  placeholder="Key"
                  value={row.paramKey}
                  onChange={handleChangeRowField(row.rowId, 'paramKey')}
                  onBlur={handleTrimOnBlur(row.rowId, 'paramKey')}
                  error={row.paramKey?.length > 64}
                  helperText={row.paramKey?.length > 64 ? `Key cannot exceed 64 characters.` : ''}
                  sx={commonInputSx}
                />
              </Grid>
              <Grid item xs={6}>
                <CustomTextField
                  fullWidth
                  placeholder="Value"
                  value={row.paramValue}
                  onChange={handleChangeRowField(row.rowId, 'paramValue')}
                  onBlur={handleTrimOnBlur(row.rowId, 'paramValue')}
                  error={row.paramValue?.length > 500}
                  helperText={row.paramValue?.length > 500 ? `Value cannot exceed 500 characters.` : ''}
                  sx={commonInputSx}
                />
              </Grid>
              <Grid item xs={1} display="flex" alignItems="center" justifyContent="flex-end">
                <IconButton
                  aria-label="remove row"
                  onClick={() => {
                    setRowToDelete(row.rowId)
                    if (row.paramKey && row.paramValue) {
                      setConfirmDeleteOpen(true);
                    } else {
                      handleRemoveRow(row.rowId)
                    }
                  }}
                  sx={{
                    ...outlinedDangerIconSx,
                    '&.Mui-disabled': {
                      opacity: 0.3,
                      borderColor: borderMuted,
                      color: borderMuted
                    }
                  }}
                  size="small"
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Grid>
            </React.Fragment>
          ))}
        </Grid>

        {/* Add Row */}
        <Box mt={2} display="flex" justifyContent="flex-start">
          <Button
            onClick={handleAddRow}
            startIcon={<AddIcon />}
            variant="text"
            sx={{
              p: 0,
              minWidth: 0,
              color: theme.palette.secondary.light,
              fontWeight: 600,
              textTransform: 'none',
              opacity: 0.8,
              '& .MuiSvgIcon-root': { color: theme.palette.secondary.light }
            }}
          >
            Add More
          </Button>
        </Box>

        {/* Actions */}
        <Box mt={4} display="flex" justifyContent="flex-end" gap={2}>
          <Button
            variant="text"
            onClick={handleOnClose}
            sx={{ color: textMuted, fontWeight: 500, textTransform: 'uppercase' }}
          >
            CANCEL
          </Button>
          <Button
            variant="pepwiseSecondary"
            onClick={handleSave}
            disabled={!selectedPipelineId || isKeyValueError}
            sx={{
              '&&&': { color: '#FFFFFF !important' },
              '& .MuiTypography-root, & .MuiButton-label, & span, & *': {
                color: '#FFFFFF !important',
                fill: '#FFFFFF !important'
              }
            }}
          >
            { onSaveLoader ? 'SAVING...' : 'SAVE' }
          </Button>
          {successMsg && (
            <Typography sx={{ ml: 2, color: 'success.main' }}>{successMsg}</Typography>
          )}
        </Box>
      </DialogContent>
    </Dialog>

    <Dialog
				open={confirmDeleteOpen}
				onClose={() => {
					setConfirmDeleteOpen(false);
					if (!open) {
						onReopen?.();
					}
				}}
				maxWidth="md"
				fullWidth
				PaperProps={{
					sx: {
						backgroundColor: '#022b5f', // Dark Blue Background
						color: 'white',
            width: 920,
            maxWidth: '92vw'
					}
				}}
			>
				<DialogTitle
					sx={{
						bgcolor: 'primary.main',
						color: 'white.main',
						p: 1,
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center'
					}}
				>
					<span>Deleting Key-Value Pair for Selected Pipeline</span>
					<IconButton onClick={() => {
						setConfirmDeleteOpen(false);
					  onReopen?.();}} sx={{ color: 'white' }}>
						<CloseIcon />
					</IconButton>
				</DialogTitle>
				<DialogContent sx={{ textAlign: 'center', padding: '20px' }}>
					<Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
						<WarningImage style={{ width: 40, height: 40, padding: 8 }} />
						<Box textAlign="left">
							<Typography variant="h6" sx={{ marginTop: '10px' }}>
								Are you sure you want to delete?
							</Typography>
							<Typography variant="bodyText" sx={{ opacity: 0.8 }}>
								Once confirmed, the action can't be undone.
							</Typography>
						</Box>
					</Box>
				</DialogContent>
				<DialogActions>
					<Button
            variant="pepwiseButtonText"
						onClick={() => {
							setConfirmDeleteOpen(false);
							onReopen?.();
						}}
						sx={{ color: '#00A3E0', textTransform: 'uppercase' }}
					>
						No, I want it!
					</Button>
					<Button
						color="error"
						onClick={() => {
							if (rowToDelete) {
								handleRemoveRow(rowToDelete);
							}
							setConfirmDeleteOpen(false);
              onReopen?.();
						}}
						variant="contained"
						sx={{ textTransform: 'uppercase' }}
					>
						Delete
					</Button>
				</DialogActions>
			</Dialog>
    </>
  );
};

export default ConfigurePipeline; 
